import { monitorPromise, pending } from "@jaybeeuu/utilities";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
export const useIsMounted = () => {
    const isMountedRef = useRef(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => (isMountedRef.current = false);
    });
    return isMountedRef;
};
const noValueSymbol = Symbol.for("no-value");
export const useSemanticMemo = (getValue, inputs) => {
    const previousInputs = useRef(inputs);
    const previousValue = useRef(noValueSymbol);
    if (previousValue.current === noValueSymbol ||
        inputs.some((input, index) => input !== previousInputs.current[index])) {
        previousValue.current = getValue();
        previousInputs.current = inputs;
    }
    return previousValue.current;
};
export const useAsyncGenerator = (makeGenerator, initialValue, deps) => {
    const [value, setValue] = useState(initialValue);
    const isMounted = useIsMounted();
    const generator = useSemanticMemo(makeGenerator, deps);
    useEffect(() => {
        void (async () => {
            for await (const nextValue of generator) {
                if (!isMounted.current) {
                    return;
                }
                setValue(nextValue);
            }
        })();
    }, [generator]);
    return value;
};
export const usePromise = (getPromise, inputs, options) => {
    const isMounted = useIsMounted();
    const abortRef = useRef(() => { });
    const [promiseState, setPromiseState] = useState(pending());
    useEffect(() => {
        const abortController = new AbortController();
        abortRef.current = () => {
            abortController.abort();
        };
        const statusGenerator = monitorPromise(getPromise({ abortSignal: abortController.signal }), options);
        void (async () => {
            for await (const nextValue of statusGenerator) {
                if (!isMounted.current) {
                    return;
                }
                setPromiseState(nextValue);
            }
        })();
        return () => {
            abortController.abort();
        };
    }, inputs);
    return {
        abort: useCallback(() => {
            abortRef.current();
        }, []),
        promiseState,
    };
};
//# sourceMappingURL=async-hooks.js.map